import { Typography } from "@material-ui/core";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import { makeStyles } from "@material-ui/core/styles";
import CardSpacer from "@saleor/components/CardSpacer";
import CardTitle from "@saleor/components/CardTitle";
import ExternalLink from "@saleor/components/ExternalLink";
// import { Hr } from "@saleor/components/Hr";
import Skeleton from "@saleor/components/Skeleton";
import { maybe } from "@saleor/misc";
import { SearchCustomers_search_edges_node } from "@saleor/searches/types/SearchCustomers";
import { FetchMoreProps, UserPermissionProps } from "@saleor/types";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { OrderDetails_order } from "../../types/OrderDetails";
import OrderAddress from "./OrderAddress";

const useStyles = makeStyles(
  theme => ({
    root: {
      ...theme.typography.body1,
      lineHeight: 1.9,
      width: "100%"
    },
    sectionHeader: {
      alignItems: "center",
      display: "flex",
      marginBottom: theme.spacing(3)
    },
    sectionHeaderTitle: {
      flex: 1,
      fontWeight: 600 as 600,
      lineHeight: 1,
      textTransform: "uppercase"
    },
    sectionHeaderToolbar: {
      marginRight: -theme.spacing(2)
    },
    subSection: {
      display: "flex",
      flexDirection: "column"
      // marginTop: "9px"
    },
    subSectionTitle: {
      flex: 1,
      fontWeight: 500,
      lineHeight: 1,
      marginTop: "0px"
    },
    textBold: {
      fontSize: "14px",
      fontWeight: 600,
      whiteSpace: "nowrap"
    },
    textRight: {
      fontSize: "14px",
      textAlign: "right",
      whiteSpace: "nowrap"
    },
    userEmail: {
      fontWeight: 600 as 600,
      marginBottom: theme.spacing(1)
    }
  }),
  { name: "OrderCustomer" }
);

export interface OrderCustomerProps
  extends Partial<FetchMoreProps>,
    UserPermissionProps {
  order: OrderDetails_order;
  users?: SearchCustomers_search_edges_node[];
  loading?: boolean;
  canEditAddresses: boolean;
  canEditCustomer: boolean;
  fetchUsers?: (query: string) => void;
  onCustomerEdit?: (data: { user?: string; userEmail?: string }) => void;
  onProfileView: () => void;
  onShippingAddressEdit?: () => void;
}

const OrderCustomer: React.FC<OrderCustomerProps> = props => {
  const { order } = props;
  const classes = useStyles(props);

  const intl = useIntl();

  const [smac, setSmac] = React.useState("");

  React.useEffect(() => {
    if (order && order.subOrders) {
      for (const subOrder of order.subOrders) {
        if (subOrder?.smac) {
          setSmac(subOrder?.smac);
          return;
        }
      }
    }
  }, [order]);

  return (
    <>
      <Card>
        <CardTitle
          customId="txtAdminFulfillmentMotherOrderDetailsPageSectionHeaderCustomer"
          title={intl.formatMessage({
            defaultMessage: "Customer",
            description: "section header"
          })}
        />
        <CardContent>
          <table className={classes.root}>
            <tbody id="cmpAdminFulfillmentMotherOrderDetailsPageCustomerDetails">
              <tr>
                <td
                  id="tblDataLabelAdminFulfillmentMotherOrderDetailsPageCustomerName"
                  className={classes.textBold}
                >
                  <FormattedMessage
                    defaultMessage="Name"
                    description="order customer name"
                  />
                </td>
                <td
                  id="tblDataTxtAdminFulfillmentMotherOrderDetailsPageCustomerName"
                  className={classes.textRight}
                >
                  {order && order.user && order.user.firstName
                    ? order.user.firstName + " "
                    : "-"}
                  {order && order.user && order.user.lastName
                    ? order.user.lastName
                    : "-"}
                </td>
              </tr>
              {/* Smac */}
              {maybe(() =>
                smac !== "" ? (
                  <tr>
                    <td
                      id="tblDataLabelAdminFulfillmentMotherOrderDetailsPageSMACNo"
                      className={classes.textBold}
                    >
                      <FormattedMessage
                        defaultMessage="SMAC No."
                        description="order customer smac number"
                      />
                    </td>
                    <td
                      id="tblDataTxtAdminFulfillmentMotherOrderDetailsPageSMACNo"
                      className={classes.textRight}
                    >
                      {smac}
                    </td>
                  </tr>
                ) : null
              )}
              <tr>
                <td
                  id="tblDataLabelAdminFulfillmentMotherOrderDetailsPageEmailAddress"
                  className={classes.textBold}
                >
                  <FormattedMessage
                    defaultMessage="Email Address"
                    description="order customer email"
                  />
                </td>
                <td
                  id="tblDataTxtAdminFulfillmentMotherOrderDetailsPageEmailAddress"
                  className={classes.textRight}
                >
                  {maybe(() => order.user.email) === undefined ? (
                    <Skeleton />
                  ) : order.user.email === null ? (
                    <Typography>
                      <FormattedMessage
                        defaultMessage="Not set"
                        description="customer is not set in order"
                        id="txtAdminFulfillmentMotherOrderDetailsPageCustomerIsNotSetInOrder"
                      />
                    </Typography>
                  ) : (
                    <ExternalLink
                      id="txtLinkAdminFulfillmentMotherOrderDetailsPageExtrnalLink"
                      href={`mailto:${maybe(() => order.user.email)}`}
                      typographyProps={{ color: "primary" }}
                    >
                      {maybe(() => order.user.email, "")}
                    </ExternalLink>
                  )}
                </td>
              </tr>
              <tr>
                <td
                  id="tblDataTxtAdminFulfillmentMotherOrderDetailsPageContactNo"
                  className={classes.textBold}
                >
                  <FormattedMessage
                    defaultMessage="Contact Number"
                    description="order customer contact number"
                    id="txtAdminFulfillmentMotherOrderDetailsPageOrderCustomerContactNumber"
                  />
                </td>
                <td
                  id="tblDataTxtAdminFulfillmentMotherOrderDetailsPageCustomerIsNotSetContactNumber"
                  className={classes.textRight}
                >
                  {maybe(() => order.user.contactNo) === undefined ? (
                    <Skeleton />
                  ) : order.user.contactNo === null ? (
                    <Typography>
                      <FormattedMessage
                        defaultMessage="Not set"
                        description="customer is not set in order"
                        id="txtAdminFulfillmentMotherOrderDetailsPageCustomerIsNotSetContactNumber"
                      />
                    </Typography>
                  ) : (
                    <ExternalLink
                      id="txtLinkAdminFulfillmentMotherOrderDetailsPageContactNumber"
                      href={`tel:${maybe(() => order.user.contactNo)}`}
                      typographyProps={{ color: "primary" }}
                    >
                      {maybe(() => order.user.contactNo, "")}
                    </ExternalLink>
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </CardContent>
        {/* <Hr /> */}
      </Card>
      <CardSpacer />
      <Card>
        <CardTitle
          customId="txtAdminFulfillmentMotherOrderDetailsPageShippingAddress"
          title={intl.formatMessage({
            defaultMessage: "Shipping Address",
            description: "shipping address header"
          })}
        />
        <CardContent id="cmpAdminFulfillmentMotherOrderDetailsPageOrderAddress">
          <OrderAddress order={order} />
        </CardContent>
      </Card>
    </>
  );
};

OrderCustomer.displayName = "OrderCustomer";
export default OrderCustomer;
